import { FC, useMemo, useState } from "react";
import { Box, Grid, LinearProgress, Stack } from "@mui/material";
import { useUserStore } from "../../../../../stores/user.store";
import useIncentiveDisbursementReport from "../../../../../hooks/useIncentiveDisbursementReport";
import BasicKPIComponent from "../../../../Dashboard/BasicKPIComponent";
import DataTable from "../../../../../components/DataTable/DataTable";
import { DataTableCell } from "../../../../../components/DataTable/dataTable.interface";
import EarnedVsPotentialChart from "../../../../../components/EarnedVsPotentialChart";
import ReportFiltersBar, {
	ReportFilters,
} from "../../components/ReportFiltersBar";
import EngagementRateBadge from "./EngagementRateBadge";
import { useFormatCurrency } from "../../../../../hooks/useSetting";

interface Filters extends ReportFilters {
	pageNumber: number;
	pageSize: number;
}

const HEAD_CELLS = [
	{
		id: "enrollmentId",
		label: "Enrollment ID",
		isSortable: false,
		disablePadding: false,
	},
	{
		id: "participantName",
		label: "Participant",
		isSortable: false,
		disablePadding: false,
	},
	{
		id: "orgUnit",
		label: "Org unit",
		isSortable: false,
		disablePadding: false,
	},
	{
		id: "researcher",
		label: "Researcher",
		isSortable: false,
		disablePadding: false,
	},
	{
		id: "potential",
		label: "Potential",
		isSortable: false,
		disablePadding: false,
		align: "right" as const,
	},
	{
		id: "earned",
		label: "Earned",
		isSortable: false,
		disablePadding: false,
		align: "right" as const,
	},
	{
		id: "received",
		label: "Received",
		isSortable: false,
		disablePadding: false,
		align: "right" as const,
	},
	{
		id: "engagementRate",
		label: "Engagement",
		isSortable: false,
		disablePadding: false,
		align: "center" as const,
	},
];

interface IncentiveDisbursementTabProps {
	programId: string;
}

const IncentiveDisbursementTab: FC<IncentiveDisbursementTabProps> = ({
	programId,
}) => {
	const { userInfo } = useUserStore();
	const accountId = userInfo?.accountId ?? "";
	const formatCurrency = useFormatCurrency();

	const [filters, setFilters] = useState<Filters>({
		orgUnitId: "",
		researcherId: "",
		startDate: null,
		endDate: null,
		pageNumber: 0,
		pageSize: 10,
	});

	const { data, isLoading, isFetching } = useIncentiveDisbursementReport({
		accountId,
		programId,
		orgUnitId: filters.orgUnitId || undefined,
		researcherId: filters.researcherId || undefined,
		startDate: filters.startDate ? filters.startDate.toISOString() : undefined,
		endDate: filters.endDate ? filters.endDate.toISOString() : undefined,
		pageNumber: filters.pageNumber,
		pageSize: filters.pageSize,
	});

	const rows = useMemo<DataTableCell[]>(() => {
		return (data?.byEnrollment ?? []).map((e, idx) => ({
			id: idx,
			enrollmentId: e.enrollmentId.slice(-6).toUpperCase(),
			participantName: e.participantName,
			orgUnit: e.orgUnit,
			researcher: e.researcher,
			potential: formatCurrency(e.potential),
			earned: formatCurrency(e.earned),
			received: formatCurrency(e.received),
			engagementRate: {
				id: e.enrollmentId,
				node: <EngagementRateBadge rate={e.engagementRate} />,
			},
		}));
	}, [data, formatCurrency]);

	return (
		<Stack spacing={3}>
			<ReportFiltersBar
				idPrefix="incentive-disbursement"
				filters={filters}
				onChange={(next) =>
					setFilters((p) => ({ ...p, ...next, pageNumber: 0 }))
				}
				availableOrgUnits={data?.availableOrgUnits ?? []}
				availableResearchers={data?.availableResearchers ?? []}
			/>

			{(isLoading || isFetching) && <LinearProgress />}

			{/* KPI cards */}
			<Grid container spacing={2}>
				<BasicKPIComponent
					title="Total earned"
					content={formatCurrency(data?.kpis.totalEarned)}
					footer={{ hidden: true }}
				/>
				<BasicKPIComponent
					title="Total received"
					content={formatCurrency(data?.kpis.totalReceived)}
					footer={{ hidden: true }}
				/>
				<BasicKPIComponent
					title="Avg earned per enrollment"
					content={formatCurrency(data?.kpis.avgEarned)}
					footer={{ hidden: true }}
				/>
				<BasicKPIComponent
					title="Potential total"
					content={formatCurrency(data?.kpis.potential)}
					footer={{ hidden: true }}
				/>
				<BasicKPIComponent
					title="Engagement rate"
					content={`${(data?.kpis.engagementRate ?? 0).toFixed(1)}%`}
					footer={{ hidden: true }}
				/>
			</Grid>

			{/* Earned vs Potential per day (reuse existing component) */}
			<EarnedVsPotentialChart data={data?.byDay} isLoading={isLoading} />

			{/* Per-enrollment breakdown */}
			<Box>
				<DataTable
					data={rows}
					rowsPerPage={filters.pageSize}
					order="desc"
					orderBy="earned"
					page={filters.pageNumber}
					headCells={HEAD_CELLS}
					total={data?.pagination.total ?? 0}
					isLoading={isLoading || isFetching}
					messageEmptyData="No enrollments match the selected filters"
					onChangePage={(_event, newPage) => {
						setFilters((p) => ({ ...p, pageNumber: newPage }));
					}}
					onChangeSize={(event) => {
						setFilters((p) => ({
							...p,
							pageNumber: 0,
							pageSize: Number(event.target.value),
						}));
					}}
				/>
			</Box>
		</Stack>
	);
};

export default IncentiveDisbursementTab;
